import React, { useState, useRef, useEffect } from 'react';
import { useConversation } from '@elevenlabs/react';
import { selectCls, inputCls, labelCls } from '../shared';
import { MOCK_AI_UPLOAD, MOCK_LEADS } from '../mockData';
import api from '../../utils/api';

const LEAD_CAPTURE_AGENT_ID = 'agent_0401kmtjrjdcey2ahvms6hgqq675';

const SAMPLE_RATE = 16000;
const MAX_RECORD_SECONDS = 900;
const AUDIO_EXTENSIONS = ['.wav', '.mp3', '.m4a', '.ogg', '.webm', '.flac', '.aac'];

function getAccessToken() {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith('access_token='))
    ?.split('=')[1];
}

function getWsBaseUrl() {
  const httpBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  return httpBase.replace(/^http/, 'ws');
}

function float32ToInt16(float32Array) {
  const int16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return int16;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function resample(data, fromRate, toRate) {
  if (fromRate === toRate) return data;
  const ratio = fromRate / toRate;
  const len = Math.round(data.length / ratio);
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    const idx = i * ratio;
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, data.length - 1);
    const f = idx - lo;
    out[i] = data[lo] * (1 - f) + data[hi] * f;
  }
  return out;
}

function formatTime(s) {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function isAudioFile(file) {
  const name = file.name.toLowerCase();
  return AUDIO_EXTENSIONS.some(ext => name.endsWith(ext)) || file.type.startsWith('audio/');
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function detectFileType(file) {
  if (!file) return 'file';
  if (file.type.startsWith('audio/') || isAudioFile(file)) return 'audio';
  if (file.type.startsWith('image/')) return 'image';
  return 'file';
}

async function processCapture({ type, transcription, date, duration, file }) {
  if (type === 'audio') {
    return api.post('/adani/process', {
      type: 'audio',
      transcription: transcription || '',
      date: date || todayISO(),
      duration: duration || 0,
    });
  }
  // Image or file — multipart form-data with the file attached
  const form = new FormData();
  form.append('type', type || 'file');
  form.append('date', date || todayISO());
  if (duration) form.append('duration', String(duration));
  if (file) form.append('file', file, file.name || 'upload');
  return api.post('/adani/process', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// ─── Shared UI Atoms ────────────────────────────────────────────────────────

function WsStatusDot({ status }) {
  const colors = {
    idle: 'bg-gray-300',
    connecting: 'bg-yellow-400 animate-pulse',
    connected: 'bg-green-500',
    streaming: 'bg-green-500 animate-pulse',
    error: 'bg-red-500',
  };
  const labels = {
    idle: 'Disconnected',
    connecting: 'Connecting…',
    connected: 'Connected',
    streaming: 'Streaming',
    error: 'Connection error',
  };
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--adani-text-tertiary)]">
      <span className={`w-1.5 h-1.5 rounded-full ${colors[status] || colors.idle}`} />
      {labels[status] || ''}
    </span>
  );
}

function SaveStatusBadge({ status }) {
  if (!status) return null;
  const map = {
    saving: { cls: 'text-[var(--adani-primary)]', label: 'Saving…' },
    saved:  { cls: 'text-[var(--adani-success)]',  label: 'Saved' },
    error:  { cls: 'text-[var(--adani-danger)]',   label: 'Save failed' },
  };
  const s = map[status] || map.saving;
  return <span className={`text-[11px] font-medium ${s.cls}`}>{s.label}</span>;
}

function TranscriptionPanel({ committed, partial, isLive, status, saveStatus }) {
  const transcript = committed.map(s => s.text).join(' ');
  if (!transcript && !partial) return null;
  return (
    <div className="glass-card-static rounded-xl p-5 mt-5 animate-slide-up">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--adani-primary)" strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span className="text-[13px] font-semibold text-[var(--adani-text-primary)]">
            {isLive ? 'Live Transcription' : 'Transcription'}
          </span>
          {committed.length > 0 && (
            <span className="text-[11px] text-[var(--adani-text-tertiary)]">
              {committed.length} segment{committed.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SaveStatusBadge status={saveStatus} />
          {status && <WsStatusDot status={status} />}
        </div>
      </div>
      <div className="text-[13px] leading-relaxed text-[var(--adani-text-secondary)] whitespace-pre-wrap">
        {transcript}
        {partial && (
          <span className="text-[var(--adani-text-tertiary)] italic">{transcript ? ' ' : ''}{partial}</span>
        )}
        {isLive && <span className="text-[var(--adani-primary)]" style={{ animation: 'pipBlink 1s step-end infinite' }}>▋</span>}
      </div>
    </div>
  );
}

// ─── Voice Recording Tab ────────────────────────────────────────────────────

function VoiceTab() {
  const [rec, setRec] = useState(false);
  const [sec, setSec] = useState(0);
  const [done, setDone] = useState(false);
  const [title, setTitle] = useState('');
  const [programme, setProgramme] = useState('');
  const [participants, setParticipants] = useState('');
  const [agenda, setAgenda] = useState('');
  const [partialText, setPartialText] = useState('');
  const [committed, setCommitted] = useState([]);
  const [wsStatus, setWsStatus] = useState('idle');
  const [saveStatus, setSaveStatus] = useState(null);

  const timerRef = useRef(null);
  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const gainRef = useRef(null);

  const committedRef = useRef([]);
  const partialRef = useRef('');
  const metaRef = useRef({});
  committedRef.current = committed;
  partialRef.current = partialText;
  metaRef.current = { title, programme, participants, agenda, sec };

  useEffect(() => () => cleanupAll(), []);

  function cleanupAudio() {
    if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null; }
    if (sourceRef.current) { sourceRef.current.disconnect(); sourceRef.current = null; }
    if (gainRef.current) { gainRef.current.disconnect(); gainRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  }

  function cleanupWs() {
    if (wsRef.current) {
      try { if (wsRef.current.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ end: true })); } catch {}
      wsRef.current.close();
      wsRef.current = null;
    }
  }

  function cleanupAll() {
    clearInterval(timerRef.current);
    cleanupAudio();
    cleanupWs();
  }

  async function saveVoiceRecording(segments) {
    const transcription = segments.map(s => s.text).join(' ');
    const m = metaRef.current;
    setSaveStatus('saving');
    try {
      await processCapture({
        type: 'audio',
        transcription,
        date: todayISO(),
        duration: m.sec,
      });
      setSaveStatus('saved');
    } catch (err) {
      console.error('Failed to save voice recording:', err);
      setSaveStatus('error');
    }
  }

  function handleWsMessage(event) {
    try {
      const msg = JSON.parse(event.data);
      switch (msg.type) {
        case 'partial':
          setPartialText(msg.text || '');
          break;
        case 'committed':
          setCommitted(prev => {
            const next = [...prev, { text: msg.text, lang: msg.language_code, words: msg.words }];
            committedRef.current = next;
            return next;
          });
          setPartialText('');
          break;
        case 'done': {
          setWsStatus('idle');
          let finalSegments = committedRef.current;
          // Fold any remaining partial text into committed so it's not lost
          if (partialRef.current) {
            const leftover = { text: partialRef.current, lang: '', words: [] };
            finalSegments = [...finalSegments, leftover];
            committedRef.current = finalSegments;
            setCommitted(finalSegments);
            setPartialText('');
          }
          if (finalSegments.length > 0) {
            saveVoiceRecording(finalSegments);
          }
          break;
        }
        default:
          break;
      }
    } catch {}
  }

  async function startRecording() {
    cleanupAll();
    setDone(false);
    setSec(0);
    setPartialText('');
    setCommitted([]);
    committedRef.current = [];
    setWsStatus('connecting');
    setSaveStatus(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true }
      });
      streamRef.current = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: SAMPLE_RATE });
      audioCtxRef.current = audioCtx;
      const actualRate = audioCtx.sampleRate;

      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;

      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      const silentGain = audioCtx.createGain();
      silentGain.gain.value = 0;
      gainRef.current = silentGain;

      source.connect(processor);
      processor.connect(silentGain);
      silentGain.connect(audioCtx.destination);

      const token = getAccessToken();
      const wsUrl = `${getWsBaseUrl()}/voice/stt/realtime?token=${encodeURIComponent(token || '')}&sample_rate=${SAMPLE_RATE}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = handleWsMessage;
      ws.onerror = () => setWsStatus('error');
      ws.onclose = () => { if (wsRef.current === ws) setWsStatus('idle'); };

      await new Promise((resolve, reject) => {
        const onOpen = () => { ws.removeEventListener('error', onErr); resolve(); };
        const onErr = (e) => { ws.removeEventListener('open', onOpen); reject(e); };
        ws.addEventListener('open', onOpen, { once: true });
        ws.addEventListener('error', onErr, { once: true });
      });

      setWsStatus('streaming');

      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        let samples = e.inputBuffer.getChannelData(0);
        if (actualRate !== SAMPLE_RATE) samples = resample(samples, actualRate, SAMPLE_RATE);
        const pcm = float32ToInt16(samples);
        const b64 = arrayBufferToBase64(pcm.buffer);
        wsRef.current.send(JSON.stringify({ audio_base_64: b64 }));
      };

      setRec(true);
      timerRef.current = setInterval(() => {
        setSec(prev => {
          if (prev >= MAX_RECORD_SECONDS - 1) {
            stopRecording();
            return MAX_RECORD_SECONDS;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (err) {
      console.error('Failed to start recording:', err);
      setWsStatus('error');
      cleanupAll();
    }
  }

  function stopRecording() {
    clearInterval(timerRef.current);
    setRec(false);
    setDone(true);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify({ end: true })); } catch {}
    }
    if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null; }
    if (sourceRef.current) { sourceRef.current.disconnect(); sourceRef.current = null; }
    if (gainRef.current) { gainRef.current.disconnect(); gainRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  }

  const fullTranscript = committed.map(s => s.text).join(' ');

  return (
    <div className="p-6">
      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Meeting Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className={inputCls} placeholder="e.g. Board Review Discussion" />
          </div>
          <div>
            <label className={labelCls}>Programme</label>
            <select value={programme} onChange={e => setProgramme(e.target.value)} className={inputCls + ' cursor-pointer'}>
              <option value="">Select programme…</option>
              <option>Adani Defence</option>
              <option>Mundra Port Expansion</option>
              <option>Adani Green Energy</option>
              <option>Adani Enterprises</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Participants</label>
            <input value={participants} onChange={e => setParticipants(e.target.value)} className={inputCls} placeholder="e.g. Ganesh Kumar, Rajesh Mehta" />
          </div>
          <div>
            <label className={labelCls}>Agenda / Context</label>
            <input value={agenda} onChange={e => setAgenda(e.target.value)} className={inputCls} placeholder="Brief context for the recording" />
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--adani-border)] pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <button
              onClick={rec ? stopRecording : startRecording}
              className={`flex h-14 w-14 items-center justify-center rounded-full transition-all cursor-pointer shrink-0 ${
                rec
                  ? 'bg-[var(--adani-danger)] text-white'
                  : 'bg-black/[0.04] text-[var(--adani-text-tertiary)] hover:bg-black/[0.07]'
              }`}
              style={rec ? { animation: 'pipPulseRed 2s infinite' } : undefined}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path d="M12 14a3 3 0 003-3V5a3 3 0 10-6 0v6a3 3 0 003 3zM19 10v1a7 7 0 01-14 0v-1M12 18v4M8 22h8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div>
              <p className="font-google text-[28px] font-semibold tabular-nums text-[var(--adani-text-primary)] leading-none">
                {formatTime(sec)}
              </p>
              <p className="text-[12px] text-[var(--adani-text-tertiary)] mt-1">
                {rec ? `Recording · ${MAX_RECORD_SECONDS - sec}s remaining` : 'Click to start · max 15 min'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SaveStatusBadge status={saveStatus} />
            <WsStatusDot status={wsStatus} />
            {!rec && !done && (
              <button onClick={startRecording} className="btn-primary text-[13px]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="10 8 16 12 10 16 10 8"/></svg>
                Start Recording
              </button>
            )}
            {rec && (
              <button onClick={stopRecording} className="text-[13px] font-medium text-white px-4 py-2 rounded-[10px] inline-flex items-center gap-1.5 cursor-pointer" style={{ background: 'var(--adani-danger)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
                Stop
              </button>
            )}
          </div>
        </div>
      </div>

      <TranscriptionPanel
        committed={committed}
        partial={partialText}
        isLive={rec}
        status={rec || wsStatus !== 'idle' ? wsStatus : null}
        saveStatus={done ? saveStatus : null}
      />

      {done && !fullTranscript && !partialText && (
        <div className="glass-card-static rounded-xl p-5 mt-5 animate-slide-up">
          <div className="flex items-center gap-2 mb-3">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--adani-warning)" strokeWidth="2"><path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-[13px] font-semibold text-[var(--adani-text-primary)]">No transcription received</span>
          </div>
          <p className="text-[12px] text-[var(--adani-text-tertiary)]">
            The recording completed but no speech was detected. Check your microphone and try again.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Upload Tab ─────────────────────────────────────────────────────────────

function UploadTab() {
  const [phase, setPhase] = useState('idle');
  const [docTitle, setDocTitle] = useState('');
  const [programme, setProgramme] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedFileType, setUploadedFileType] = useState('file');

  const [partialText, setPartialText] = useState('');
  const [committed, setCommitted] = useState([]);
  const [wsStatus, setWsStatus] = useState('idle');
  const [saveStatus, setSaveStatus] = useState(null);

  const inp = useRef(null);
  const wsRef = useRef(null);
  const committedRef = useRef([]);
  const partialRef = useRef('');
  const metaRef = useRef({});
  const fileRef = useRef(null);
  committedRef.current = committed;
  partialRef.current = partialText;
  metaRef.current = { docTitle, programme, uploadedFileName, uploadedFileType };

  useEffect(() => () => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
  }, []);

  const [resultNotice, setResultNotice] = useState('');

  async function saveUploadRecording(segments, fileType, audioDuration) {
    const transcription = segments.map(s => s.text).join(' ');
    setSaveStatus('saving');
    try {
      if (fileType === 'audio') {
        await processCapture({
          type: 'audio',
          transcription,
          date: todayISO(),
          duration: audioDuration || 0,
        });
        setResultNotice('Recordings added successfully, redirection: Recordings');
      } else {
        await processCapture({
          type: fileType,
          date: todayISO(),
          file: fileRef.current,
        });
        setResultNotice('Leads generated Successfully, redirection: Leads');
      }
      setSaveStatus('saved');
    } catch (err) {
      console.error('Failed to save upload recording:', err);
      setSaveStatus('error');
    }
  }

  function handleWsMessage(event) {
    try {
      const msg = JSON.parse(event.data);
      switch (msg.type) {
        case 'partial':
          setPartialText(msg.text || '');
          break;
        case 'committed':
          setCommitted(prev => {
            const next = [...prev, { text: msg.text, lang: msg.language_code, words: msg.words }];
            committedRef.current = next;
            return next;
          });
          setPartialText('');
          break;
        case 'done': {
          setWsStatus('idle');
          setPhase('done');
          let finalSegments = committedRef.current;
          if (partialRef.current) {
            const leftover = { text: partialRef.current, lang: '', words: [] };
            finalSegments = [...finalSegments, leftover];
            committedRef.current = finalSegments;
            setCommitted(finalSegments);
            setPartialText('');
          }
          saveUploadRecording(finalSegments, 'audio', audioDurRef.current);
          break;
        }
        default:
          break;
      }
    } catch {}
  }

  const audioDurRef = useRef(0);

  async function processAudioFile(file) {
    setPhase('processing');
    setUploadedFileName(file.name);
    setUploadedFileType('audio');
    fileRef.current = file;
    setPartialText('');
    setCommitted([]);
    committedRef.current = [];
    setWsStatus('connecting');
    setSaveStatus(null);

    try {
      const arrayBuf = await file.arrayBuffer();
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: SAMPLE_RATE });
      const audioBuf = await audioCtx.decodeAudioData(arrayBuf);
      audioDurRef.current = Math.round(audioBuf.duration);
      let pcmFloat = audioBuf.getChannelData(0);

      if (audioBuf.sampleRate !== SAMPLE_RATE) {
        pcmFloat = resample(pcmFloat, audioBuf.sampleRate, SAMPLE_RATE);
      }
      audioCtx.close().catch(() => {});

      const token = getAccessToken();
      const wsUrl = `${getWsBaseUrl()}/voice/stt/realtime?token=${encodeURIComponent(token || '')}&sample_rate=${SAMPLE_RATE}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = handleWsMessage;
      ws.onerror = () => { setWsStatus('error'); setPhase('done'); };
      ws.onclose = () => { if (wsRef.current === ws) setWsStatus('idle'); };

      await new Promise((resolve, reject) => {
        const onOpen = () => { ws.removeEventListener('error', onErr); resolve(); };
        const onErr = (e) => { ws.removeEventListener('open', onOpen); reject(e); };
        ws.addEventListener('open', onOpen, { once: true });
        ws.addEventListener('error', onErr, { once: true });
      });

      setWsStatus('streaming');

      const CHUNK = 4096;
      for (let i = 0; i < pcmFloat.length; i += CHUNK) {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) break;
        const slice = pcmFloat.subarray(i, Math.min(i + CHUNK, pcmFloat.length));
        const pcm16 = float32ToInt16(slice);
        const b64 = arrayBufferToBase64(pcm16.buffer);
        ws.send(JSON.stringify({ audio_base_64: b64 }));
        if (i + CHUNK < pcmFloat.length) {
          await new Promise(r => setTimeout(r, Math.floor((CHUNK / SAMPLE_RATE) * 1000 * 0.5)));
        }
      }

      ws.send(JSON.stringify({ end: true }));

    } catch (err) {
      console.error('Audio file processing failed:', err);
      setWsStatus('error');
      setPhase('done');
    }
  }

  async function handleNonAudioFile(file) {
    const fileType = file.type.startsWith('image/') ? 'image' : 'file';
    setPhase('processing');
    setUploadedFileType(fileType);
    fileRef.current = file;
    setCommitted([]);
    setPartialText('');
    setSaveStatus('saving');
    try {
      await processCapture({
        type: fileType,
        date: todayISO(),
        file,
      });
      setSaveStatus('saved');
    } catch (err) {
      console.error('Failed to save file recording:', err);
      setSaveStatus('error');
    }
    setPhase('done');
  }

  function handleFile(file) {
    if (!file) return;
    setResultNotice('');
    setUploadedFileName(file.name);
    if (isAudioFile(file)) {
      processAudioFile(file);
    } else {
      handleNonAudioFile(file);
    }
  }

  function reset() {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setPhase('idle');
    setPartialText('');
    setCommitted([]);
    committedRef.current = [];
    setWsStatus('idle');
    setSaveStatus(null);
    setUploadedFileName('');
    setUploadedFileType('file');
    fileRef.current = null;
    audioDurRef.current = 0;
    if (inp.current) inp.current.value = '';
    setResultNotice('');
  }

  const hasTranscript = committed.length > 0;

  return (
    <div className="p-6">
      <input ref={inp} type="file" className="hidden" accept=".pdf,image/*,audio/*,.wav,.mp3,.m4a,.ogg,.webm,.flac,.aac" onChange={e => handleFile(e.target.files?.[0])} />
      <div className="space-y-4 mb-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Document Title</label>
            <input value={docTitle} onChange={e => setDocTitle(e.target.value)} className={inputCls} placeholder="e.g. Site Inspection Notes" />
          </div>
          <div>
            <label className={labelCls}>Programme</label>
            <select value={programme} onChange={e => setProgramme(e.target.value)} className={inputCls + ' cursor-pointer'}>
              <option value="">Select programme…</option>
              <option>Adani Defence</option>
              <option>Mundra Port Expansion</option>
              <option>Adani Green Energy</option>
            </select>
          </div>
        </div>
      </div>

      {phase !== 'done' ? (
        <>
          <button
            onClick={() => inp.current?.click()}
            disabled={phase === 'processing'}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); if (phase === 'idle') handleFile(e.dataTransfer.files?.[0]); }}
            className="w-full cursor-pointer rounded-xl border-2 border-dashed border-[var(--adani-border)] p-8 text-center transition-all hover:border-[var(--adani-primary)]/30 disabled:cursor-wait disabled:opacity-70"
          >
            {phase === 'processing' ? (
              <div className="flex flex-col items-center gap-2">
                <div className="h-8 w-8 rounded-full border-2 border-[var(--adani-primary)]/30 border-t-[var(--adani-primary)]" style={{ animation: 'pipSpin 0.8s linear infinite' }} />
                <p className="text-[13px] text-[var(--adani-text-secondary)]">
                  {wsStatus === 'streaming' ? `Transcribing ${uploadedFileName}…` : 'Processing…'}
                </p>
                <SaveStatusBadge status={saveStatus} />
              </div>
            ) : (
              <>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-[var(--adani-primary)]"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <p className="mt-2 text-[13px] font-medium text-[var(--adani-text-primary)]">Drop files here or click to browse</p>
                <p className="mt-0.5 text-[11px] text-[var(--adani-text-tertiary)]">PDF, Images, or Audio files (WAV, MP3, M4A, OGG, WebM)</p>
              </>
            )}
          </button>
          {phase === 'idle' && (
            <button onClick={() => inp.current?.click()} className="btn-primary w-full mt-4 text-[13px]">Upload &amp; Process</button>
          )}

          {phase === 'processing' && (
            <TranscriptionPanel
              committed={committed}
              partial={partialText}
              isLive={wsStatus === 'streaming'}
              status={wsStatus}
              saveStatus={null}
            />
          )}
        </>
      ) : (
        <div className="animate-slide-up space-y-3">
          {resultNotice ? (
            <div className="glass-card-static rounded-xl p-5">
              <p className="text-[13px] font-semibold text-[var(--adani-text-primary)]">{resultNotice}</p>
            </div>
          ) : (
            <div className="glass-card-static rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-semibold text-[var(--adani-text-primary)]">Processed</span>
                <SaveStatusBadge status={saveStatus} />
              </div>
              <p className="text-[12px] text-[var(--adani-text-secondary)]">Your file has been processed successfully.</p>
            </div>
          )}

          {hasTranscript && (
            <TranscriptionPanel
              committed={committed}
              partial=""
              isLive={false}
              status={null}
              saveStatus={saveStatus}
            />
          )}

          <button onClick={reset} className="btn-secondary text-[12px] mt-1">Upload another</button>
        </div>
      )}
    </div>
  );
}

// ─── Attach Tab ─────────────────────────────────────────────────────────────

function AttachTab() {
  const [leadId, setLeadId] = useState(MOCK_LEADS[0]?.id || '');
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState('');
  const [savingIds, setSavingIds] = useState(new Set());
  const [savedIds, setSavedIds] = useState(new Set());
  const [errorIds, setErrorIds] = useState(new Set());
  const inp = useRef(null);
  const lead = MOCK_LEADS.find(l => l.id === leadId);

  async function saveAttachment(file, leadName, fileIndex) {
    setSavingIds(prev => new Set(prev).add(fileIndex));
    try {
      await processCapture({
        type: detectFileType(file),
        date: todayISO(),
        file,
      });
      setSavingIds(prev => { const s = new Set(prev); s.delete(fileIndex); return s; });
      setSavedIds(prev => new Set(prev).add(fileIndex));
    } catch (err) {
      console.error('Failed to save attachment:', err);
      setSavingIds(prev => { const s = new Set(prev); s.delete(fileIndex); return s; });
      setErrorIds(prev => new Set(prev).add(fileIndex));
    }
  }

  function addFiles(newFiles) {
    const leadName = lead?.name || leadId;
    const startIdx = files.length;
    const mapped = Array.from(newFiles).map((f, i) => ({ file: f, lead: leadName, idx: startIdx + i }));
    setFiles(p => [...p, ...mapped]);
    mapped.forEach(m => saveAttachment(m.file, leadName, m.idx));
  }

  return (
    <div className="p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Link to Lead</label>
          <select className={selectCls} value={leadId} onChange={e => setLeadId(e.target.value)}>
            {MOCK_LEADS.map(l => <option key={l.id} value={l.id}>{l.name} — {l.company}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Notes</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} className={inputCls} placeholder="Context for these attachments" />
        </div>
      </div>
      <input ref={inp} type="file" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
      <button onClick={() => inp.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }} className="w-full cursor-pointer rounded-xl border-2 border-dashed border-[var(--adani-border)] p-8 text-center transition-all hover:border-[var(--adani-primary)]/30">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-[var(--adani-primary)]"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" strokeLinecap="round" strokeLinejoin="round"/></svg>
        <p className="mt-2 text-[13px] font-medium text-[var(--adani-text-primary)]">Drop files here or click to attach</p>
        <p className="mt-0.5 text-[11px] text-[var(--adani-text-tertiary)]">Any file type · multiple files supported</p>
      </button>
      {files.length > 0 && (
        <div className="glass-card-static rounded-xl overflow-hidden animate-slide-up">
          {files.map((a, i) => (
            <div key={i} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--adani-border)] last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-[var(--adani-text-primary)] truncate">{a.file.name}</p>
                <p className="text-[11px] text-[var(--adani-text-tertiary)]">Linked to {a.lead}</p>
              </div>
              {savingIds.has(a.idx) && <span className="text-[11px] text-[var(--adani-primary)]">Saving…</span>}
              {savedIds.has(a.idx) && <span className="text-[var(--adani-success)]">✓</span>}
              {errorIds.has(a.idx) && <span className="text-[11px] text-[var(--adani-danger)]">Failed</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Call Tab (ElevenLabs Agent) ─────────────────────────────────────────────

function CallTab() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null);
  const [callEnded, setCallEnded] = useState(false);

  const messagesRef = useRef([]);
  const startTimeRef = useRef(null);
  messagesRef.current = messages;

  const processConversation = async (conversationMessages) => {
    if (!conversationMessages || conversationMessages.length === 0) {
      console.log('No conversation to process');
      return;
    }

    const transcription = conversationMessages
      .map(m => `${m.source === 'user' ? 'User' : 'Agent'}: ${m.message}`)
      .join('\n');

    const duration = startTimeRef.current
      ? Math.round((Date.now() - startTimeRef.current) / 1000)
      : 0;

    setSaveStatus('saving');
    try {
      await processCapture({
        type: 'audio',
        transcription,
        date: todayISO(),
        duration,
      });
      setSaveStatus('saved');
    } catch (err) {
      console.error('Failed to process call conversation:', err);
      setSaveStatus('error');
    }
  };

  const conversation = useConversation({
    onConnect: () => {
      setIsConnecting(false);
      startTimeRef.current = Date.now();
      setMessages([]);
      setSaveStatus(null);
      setCallEnded(false);
    },
    onDisconnect: () => {
      setIsConnecting(false);
      if (messagesRef.current.length > 0) {
        setCallEnded(true);
        processConversation(messagesRef.current);
      }
    },
    onMessage: (message) => {
      if (message && message.message) {
        setMessages(prev => [...prev, {
          source: message.source || 'agent',
          message: message.message,
          timestamp: Date.now(),
        }]);
      }
    },
    onError: (error) => {
      if (!error.message?.includes('v1 RTC path not found')) {
        console.error('Call error:', error);
      }
      setIsConnecting(false);
    },
  });

  const { status, isSpeaking } = conversation;
  const isConnected = status === 'connected';

  const handleStart = async () => {
    if (isConnecting || status === 'connected' || status === 'connecting') return;
    setIsConnecting(true);
    setCallEnded(false);
    setSaveStatus(null);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await conversation.startSession({
        agentId: LEAD_CAPTURE_AGENT_ID,
        connectionType: 'websocket',
      });
    } catch (error) {
      console.error('Failed to start call:', error);
      setIsConnecting(false);
    }
  };

  const handleEnd = async () => {
    if (isConnected) {
      await conversation.endSession();
    }
  };

  const handleNewCall = () => {
    setMessages([]);
    setSaveStatus(null);
    setCallEnded(false);
  };

  return (
    <div className="p-6">
      <div className="flex flex-col items-center justify-center min-h-[280px]">
        <div className={`relative w-24 h-24 rounded-full flex items-center justify-center mb-6 transition-all ${isConnected ? 'bg-[var(--adani-primary)]/10' : 'bg-[var(--adani-bg-tertiary)]'}`}>
          {isConnected && (
            <span className="absolute inset-0 rounded-full border-2 border-[var(--adani-primary)] animate-ping opacity-30" />
          )}
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={isConnected ? 'var(--adani-primary)' : 'var(--adani-text-tertiary)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </div>

        <h2 className="text-[16px] font-semibold text-[var(--adani-text-primary)] mb-1">Lead Capture Agent</h2>
        <p className="text-[13px] text-[var(--adani-text-secondary)] mb-6 text-center max-w-xs">
          {isConnecting
            ? 'Connecting to agent…'
            : isConnected
            ? isSpeaking
              ? 'Agent is speaking…'
              : 'Connected — speak to capture lead info'
            : callEnded && saveStatus
            ? saveStatus === 'saving'
              ? 'Processing conversation…'
              : saveStatus === 'saved'
              ? 'Call ended — lead processed successfully'
              : 'Call ended — failed to process'
            : 'Start a call with the AI agent to capture lead details via voice'}
        </p>

        <div className="flex items-center gap-3">
          {!isConnected && !callEnded ? (
            <button
              onClick={handleStart}
              disabled={isConnecting}
              className="btn-primary px-8 py-3 text-[14px] inline-flex items-center gap-2 disabled:opacity-60"
            >
              {isConnecting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Connecting…
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  Start Call
                </>
              )}
            </button>
          ) : isConnected ? (
            <button
              onClick={handleEnd}
              className="btn-danger px-8 py-3 text-[14px] inline-flex items-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                <line x1="23" y1="1" x2="1" y2="23" />
              </svg>
              End Call
            </button>
          ) : (
            <button
              onClick={handleNewCall}
              className="btn-primary px-8 py-3 text-[14px] inline-flex items-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              New Call
            </button>
          )}
        </div>

        {isConnected && (
          <div className="mt-6 flex items-center gap-2 text-[12px] text-[var(--adani-text-tertiary)]">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Call in progress
          </div>
        )}

        {saveStatus === 'saving' && (
          <div className="mt-6 flex items-center gap-2 text-[12px] text-[var(--adani-primary)]">
            <span className="w-4 h-4 border-2 border-[var(--adani-primary)]/30 border-t-[var(--adani-primary)] rounded-full animate-spin" />
            Processing lead…
          </div>
        )}
      </div>

      {/* Live conversation transcript */}
      {(isConnected || callEnded) && messages.length > 0 && (
        <div className="glass-card-static rounded-xl p-5 mt-5 animate-slide-up">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--adani-primary)" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-[13px] font-semibold text-[var(--adani-text-primary)]">
                {isConnected ? 'Live Conversation' : 'Conversation Transcript'}
              </span>
              <span className="text-[11px] text-[var(--adani-text-tertiary)]">
                {messages.length} message{messages.length !== 1 ? 's' : ''}
              </span>
            </div>
            {saveStatus && <SaveStatusBadge status={saveStatus} />}
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {messages.map((m, i) => (
              <div key={i} className={`text-[13px] leading-relaxed ${m.source === 'user' ? 'text-[var(--adani-text-primary)]' : 'text-[var(--adani-text-secondary)]'}`}>
                <span className={`font-medium ${m.source === 'user' ? 'text-[var(--adani-primary)]' : 'text-[var(--adani-success)]'}`}>
                  {m.source === 'user' ? 'You' : 'Agent'}:
                </span>{' '}
                {m.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tabs & Main ────────────────────────────────────────────────────────────

const TAB_ICONS = {
  upload: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  attach: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>,
  call: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>,
};

const TABS = [
  { id: 'voice', label: 'Voice Recording', dot: true },
  { id: 'upload', label: 'Upload Notes', iconKey: 'upload' },
  { id: 'attach', label: 'Attachments', iconKey: 'attach' },
  { id: 'call', label: 'Call Agent', iconKey: 'call' },
];

export default function CapturePage({ openModal }) {
  const [tab, setTab] = useState('voice');

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">Capture</h1>
        <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1.5">Record conversations or upload notes to extract programme data with AI</p>
      </div>

      <div className="mb-6">
        <div className="glass-card-static inline-flex rounded-xl p-1.5 gap-2">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 rounded-lg px-6 py-2.5 text-[13px] font-medium cursor-pointer transition-all ${tab === t.id ? 'shadow-sm' : 'text-[var(--adani-text-secondary)] hover:bg-black/[0.03]'}`} style={tab === t.id ? { background: 'var(--adani-gradient)', color: '#fff' } : undefined}>
              {t.dot && <span className={`w-2 h-2 rounded-full ${tab === t.id ? 'bg-white' : 'bg-[var(--adani-primary)]'}`} />}
              {t.iconKey && TAB_ICONS[t.iconKey]}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_280px]">
        <div className="glass-card-static rounded-2xl overflow-hidden">
          {tab === 'voice' && <VoiceTab />}
          {tab === 'upload' && <UploadTab />}
          {tab === 'attach' && <AttachTab />}
          {tab === 'call' && <CallTab />}
        </div>

        <div className="space-y-5">
          <div className="glass-card-static rounded-2xl p-5">
            <h3 className="text-[14px] font-semibold text-[var(--adani-text-primary)] mb-4">Quick Actions</h3>
            <div className="space-y-1">
              {[
                ['📅', 'New Meeting', 'new-lead'],
                ['🔗', 'Link to Old Meeting', 'link-lead'],
                ['＋', 'Create New Programme', null],
                ['◆', 'View All Programmes', null],
              ].map(([ic, lbl, modal]) => (
                <button key={lbl} onClick={() => modal ? openModal(modal) : undefined} className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-[13px] font-medium text-[var(--adani-text-primary)] hover:bg-black/[0.03] cursor-pointer transition-colors">
                  <span className="text-[14px] w-5 text-center shrink-0">{ic}</span>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="glass-card-static rounded-2xl p-5">
            <h3 className="text-[14px] font-semibold text-[var(--adani-text-primary)] mb-2">View History</h3>
            <p className="text-[13px] text-[var(--adani-text-tertiary)] text-center py-6">No history yet.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
